import type { Columns, ComponentData } from '@/types/table';
import { TableStateType } from '@/types/property.types';
import { IconComponent } from '@/util/icons';
import { convertBooleanValue } from '@/util/formatting';
import { PROPERTY_COLUMNS, ORDER_COLUMNS } from '../metadata/columns';
import { States } from '../States';

import error from '@/asset/icons/file-error-icon.svg';
import success from '@/asset/icons/file-success-icon.svg';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

export function processTableColumns(isProperty: boolean): Columns[] {
  return isProperty ? PROPERTY_COLUMNS : ORDER_COLUMNS;
}

export function getTableData(
  isProperty: boolean,
  apiData: Record<string, unknown>[]
): Record<string, unknown>[] {
  return apiData.map((data) => patchPropertyData(isProperty, data));
}

// ---------------------------------------------------------------------------
// Row builder
// ---------------------------------------------------------------------------

function getStateValue(placed: boolean): string {
  return placed ? 'placed' : 'accepted';
}

function getStateType(placed: boolean): TableStateType {
  return placed ? TableStateType.SUCCESS : TableStateType.NO_ERROR;
}

export function patchPropertyData(
  isProperty: boolean,
  data: Record<string, unknown>
): Record<string, unknown> {
  // Both services serialize uploadedDate as an ISO-8601 UTC string (bare
  // Instant under Spring Boot defaults; order-service pins the same shape
  // with @JsonFormat). The property branch used to parse it as epoch
  // seconds (dayjs.unix), which renders "Invalid Date" for the real wire.
  const dateUploaded = dayjs.utc(data.uploadedDate as string).local();

  const orderRejected = data.orderRejected as number;
  const orderApproved = data.orderApproved as number;
  const orderPlaced = data.orderPlaced as boolean | number;

  return {
    id: data.fileId,
    fileId: data.fileId,
    // DV-2289: S3 key of the archived source spreadsheet (order uploads only; null when archival
    // is off or the upload predates it). Drives the per-row Download action.
    sourceFileKey: data.sourceFileKey,
    state: {
      component: (
        <IconComponent
          src={orderRejected ? error : success}
          alt="success"
        />
      ),
      data: orderRejected ? 'rejected' : 'placed'
    },
    fileName: data.fileName,
    uploadedTimestamp: dateUploaded.format('MMM D, YYYY h:mm A'),
    uploadedDate: dateUploaded.format('LL'),
    uploadedTime: dateUploaded.format('LT'),
    uploadedBy: data.uploadedBy,
    subTotal: isProperty ? '' : data.subTotal,
    failCount: orderRejected,
    orderPlaced,
    successCount: isProperty ? orderPlaced : orderApproved,
    success: {
      component: (
        <States
          type={
            isProperty
              ? TableStateType.SUCCESS
              : getStateType(orderPlaced as boolean)
          }
          text={`${isProperty ? orderPlaced : orderApproved} ${
            isProperty ? 'added' : getStateValue(orderPlaced as boolean)
          }`}
        />
      ),
      data: isProperty ? orderPlaced : orderApproved
    } as ComponentData,
    failed: orderRejected
      ? ({
          component: (
            <States
              type={TableStateType.ERROR}
              text={`${orderRejected} ${
                isProperty ? 'failed' : 'rejected'
              }`}
            />
          ),
          data: orderRejected
        } as ComponentData)
      : null,
    action: null
  };
}
